/**
 * Cascade the real, offered casing variants before the native picker handles
 * the change. This payload contains option identities only, never prices.
 * Shopify still renders the selected variant, price, inventory and purchase UI.
 */
class HPCasingsOptions extends HTMLElement {
  #abortController;

  connectedCallback() {
    this.#abortController?.abort();
    this.#abortController = new AbortController();
    this.addEventListener('change', this.#onChange, {
      capture: true,
      signal: this.#abortController.signal,
    });
  }

  disconnectedCallback() {
    this.#abortController?.abort();
  }

  #onChange = (event) => {
    const target = event.target;
    if (!(target instanceof HTMLSelectElement) || !target.hasAttribute('data-casings-position')) return;

    const selects = Array.from(this.querySelectorAll('select[data-casings-position]'));
    const changedIndex = selects.indexOf(target);
    let data;
    try {
      // Read on each change because Shopify morphs this content after a request.
      data = JSON.parse(this.querySelector('[data-casings-options]')?.textContent || 'null');
    } catch {
      return;
    }
    if (!data?.variants?.length || data.options?.length !== selects.length || changedIndex < 0) return;

    // Both dimensions are labelled in millimetres. Sort the numeric measure,
    // not its text, and keep designations such as WW63 after numeric diameters.
    const numericMeasure = (value) => {
      const match = value.trim().replace(/^Ø\s*/, '').match(/^(\d+(?:[.,]\d+)?)/);
      return match ? Number(match[1].replace(',', '.')) : Infinity;
    };
    const optionValues = data.options.map((option, index) =>
      index < 2
        ? [...option.values].sort((a, b) => numericMeasure(a.value) - numericMeasure(b.value))
        : option.values
    );

    const previousValues = selects.map((select) => select.value);
    let candidates = data.variants;
    const choices = [];

    for (let index = 0; index < selects.length; index += 1) {
      const offeredValues = new Set(candidates.map((variant) => variant.options[index]));
      const desiredValue = previousValues[index];
      const value = offeredValues.has(desiredValue)
        ? desiredValue
        : optionValues[index].find((entry) => offeredValues.has(entry.value))?.value;
      if (value === undefined) return;
      choices.push(value);
      candidates = candidates.filter((variant) => variant.options[index] === value);
    }

    const selectedVariant = candidates[0];
    if (!selectedVariant) return;

    // Preserve the select elements and native IDs/refs. Only their options vary.
    // Rebuilding removes unavailable choices entirely, including on mobile.
    let prefixCandidates = data.variants;
    selects.forEach((select, index) => {
      const offeredValues = new Set(prefixCandidates.map((variant) => variant.options[index]));
      const options = optionValues[index]
        .filter((entry) => offeredValues.has(entry.value))
        .map((entry) => {
          const option = new Option(entry.value, entry.value, entry.value === choices[index], entry.value === choices[index]);
          option.dataset.optionValueId = entry.id;
          option.dataset.inputId = `${index + 1}-${data.options[index].values.indexOf(entry)}`;
          option.dataset.connectedProductUrl = entry.productUrl || '';
          option.dataset.variantId = selectedVariant.id;
          return option;
        });
      select.replaceChildren(...options);
      prefixCandidates = prefixCandidates.filter((variant) => variant.options[index] === choices[index]);
    });

    // Let the original event bubble. The native picker now requests all three
    // valid option IDs, updates the URL and emits its normal selected/update events.
  };
}

if (!customElements.get('hp-casings-options')) {
  customElements.define('hp-casings-options', HPCasingsOptions);
}
